import { XCircle, CheckCircle, Zap, CloudOff, Server, DollarSign } from 'lucide-react';
import PageLayout from '../components/PageLayout';
import { GlassCard } from '../components/ui/Glass';
import { Link } from 'react-router-dom';

const Competitors = () => {
    return (
        <PageLayout
            title="FRB vs Telegram Bots - The Comparison"
            description="Stop feeding the middleman. Compare FRB (Local C#) against slow Python Telegram bots. Solana vs Ethereum MEV profit metrics included."
            pageTitle="FRB vs The Rest"
        >
            <div className="space-y-16">

                {/* Aggressive Hero */}
                <section className="text-center max-w-5xl mx-auto space-y-6">
                    <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight leading-none">
                        Why pay 1% fees on <span className="text-red-500 line-through decoration-4 decoration-white">Telegram?</span>
                    </h1>
                    <p className="text-2xl text-gray-300 font-medium max-w-3xl mx-auto">
                        If your bot asks for your private key, <span className="text-red-400 font-bold">it's not your bot.</span>
                    </p>
                </section>

                {/* The Comparison Table */}
                <section className="overflow-hidden rounded-2xl border border-gray-700 shadow-2xl">
                    <div className="grid grid-cols-1 md:grid-cols-3 bg-[#0D1626]">

                        {/* Header / Labels - Only visible on desktop or inline on mobile */}
                        <div className="hidden md:flex flex-col p-6 border-r border-gray-800 justify-center space-y-8 text-right text-gray-400 font-semibold text-lg py-32">
                            <div className="h-12 flex items-center justify-end">Custody of Keys</div>
                            <div className="h-12 flex items-center justify-end">Execution Speed</div>
                            <div className="h-12 flex items-center justify-end">Fee on Profit</div>
                            <div className="h-12 flex items-center justify-end">Mempool Scope</div>
                            <div className="h-12 flex items-center justify-end">Infrastructure</div>
                        </div>

                        {/* Telegram Bots (Villain) */}
                        <div className="flex flex-col p-8 border-b md:border-b-0 md:border-r border-gray-800 bg-red-900/5 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-10">
                                <XCircle className="w-32 h-32" />
                            </div>
                            <div className="text-center mb-10 z-10">
                                <h2 className="text-2xl font-bold text-gray-300">Generic Telegram Bots</h2>
                                <p className="text-sm text-red-400 mt-2">Maestro, Unibot, Banana*</p>
                            </div>

                            <div className="space-y-8 z-10">
                                {/* Mobile Label */}
                                <div className="flex md:hidden text-xs uppercase text-gray-500 font-bold mb-1">Custody</div>
                                <div className="h-12 flex items-center gap-3 text-gray-300 text-lg">
                                    <CloudOff className="w-6 h-6 text-red-500" />
                                    Custodial (Cloud Database)
                                </div>

                                <div className="flex md:hidden text-xs uppercase text-gray-500 font-bold mb-1">Speed</div>
                                <div className="h-12 flex items-center gap-3 text-gray-300 text-lg">
                                    <div className="w-6 h-6 flex items-center justify-center text-red-500 font-bold">~2s</div>
                                    Slow (Shared Queue)
                                </div>

                                <div className="flex md:hidden text-xs uppercase text-gray-500 font-bold mb-1">Fees</div>
                                <div className="h-12 flex items-center gap-3 text-gray-300 text-lg">
                                    <DollarSign className="w-6 h-6 text-red-500" />
                                    1% on EVERY trade
                                </div>

                                <div className="flex md:hidden text-xs uppercase text-gray-500 font-bold mb-1">Scope</div>
                                <div className="h-12 flex items-center gap-3 text-gray-300 text-lg">
                                    <span className="text-red-400">Restricted</span> by their RPC limits
                                </div>

                                <div className="flex md:hidden text-xs uppercase text-gray-500 font-bold mb-1">Infra</div>
                                <div className="h-12 flex items-center gap-3 text-gray-300 text-lg">
                                    Python / Node.js (High Latency)
                                </div>
                            </div>
                        </div>

                        {/* FRB (Hero) */}
                        <div className="flex flex-col p-8 bg-blue-900/10 relative overflow-hidden border-2 border-blue-500/50 rounded-xl md:rounded-none md:-m-1 md:z-10 shadow-[0_0_50px_rgba(59,130,246,0.3)] transform scale-100 md:scale-105">
                            <div className="absolute top-0 right-0 p-4 opacity-10">
                                <CheckCircle className="w-32 h-32" />
                            </div>
                            <div className="text-center mb-10 z-10">
                                <h2 className="text-3xl font-extrabold text-white">FRB Agent</h2>
                                <p className="text-sm text-blue-400 mt-2">Professional Grade</p>
                            </div>

                            <div className="space-y-8 z-10">
                                <div className="flex md:hidden text-xs uppercase text-blue-300 font-bold mb-1">Custody</div>
                                <div className="h-12 flex items-center gap-3 text-white text-lg font-bold">
                                    <Server className="w-6 h-6 text-green-400" />
                                    Non-Custodial (Localhost)
                                </div>

                                <div className="flex md:hidden text-xs uppercase text-blue-300 font-bold mb-1">Speed</div>
                                <div className="h-12 flex items-center gap-3 text-white text-lg font-bold">
                                    <Zap className="w-6 h-6 text-yellow-400" />
                                    &lt;50ms (Direct Mempool)
                                </div>

                                <div className="flex md:hidden text-xs uppercase text-blue-300 font-bold mb-1">Fees</div>
                                <div className="h-12 flex items-center gap-3 text-white text-lg font-bold">
                                    <span className="text-green-400">0% Protocol Fee</span> **
                                </div>

                                <div className="flex md:hidden text-xs uppercase text-blue-300 font-bold mb-1">Scope</div>
                                <div className="h-12 flex items-center gap-3 text-white text-lg font-bold">
                                    Unlimited (Custom Nodes)
                                </div>

                                <div className="flex md:hidden text-xs uppercase text-blue-300 font-bold mb-1">Infra</div>
                                <div className="h-12 flex items-center gap-3 text-white text-lg font-bold">
                                    C# / Rust (Native Speed)
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className="bg-blue-950/50 p-4 text-center text-xs text-gray-400">
                        * Comparison based on public documentation of major Telegram bots as of 2025. ** You keep 100% of profit, you only pay standard gas/builder tips.
                    </div>
                </section>

                {/* Narrative Block: Solana vs Ethereum */}
                <section className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8 items-center">
                    <div className="space-y-4">
                        <h3 className="text-2xl font-bold text-white">Solana vs Ethereum MEV Profit</h3>
                        <p className="text-gray-300 leading-relaxed">
                            While <strong>Solana MEV</strong> is becoming crowded with Jito tip wars, the Ethereum and L2 (Base, Arbitrum) landscape remains the king of atomic arbitrage.
                        </p>
                        <p className="text-gray-300 leading-relaxed">
                            FRB is optimized for EVM chains where the implementation of <strong>Flashbots bundles</strong> guarantees that failed transactions don't cost you gas. Telegram bots on Solana often span high failure rates. Stick to deterministic execution.
                        </p>
                    </div>
                    <GlassCard className="p-6 text-center space-y-4">
                        <div className="text-4xl font-black text-white">14.2 ETH</div>
                        <div className="text-sm uppercase tracking-widest text-gray-500">Avg. Monthly User Profit (Top 10%)</div>
                        <div className="h-1 w-24 bg-blue-600 mx-auto rounded-full"></div>
                        <p className="text-xs text-gray-400">Verified on-chain via Builder payouts</p>
                    </GlassCard>
                </section>

                {/* CTA */}
                <div className="text-center py-12">
                    <h2 className="text-3xl font-bold text-white mb-6">Stop Feeding the Middleman.</h2>
                    <Link to="/install" className="btn-primary px-10 py-5 text-xl shadow-xl shadow-blue-600/20 hover:scale-105 transition-transform">
                        Download FRB for Windows
                    </Link>
                </div>

            </div>
        </PageLayout>
    );
};

export default Competitors;
